import { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { hourValueText, weekdayNames } from './chartText';
import { mixHex, useChartTokens } from './chartTokens';
import { formatDecimal, formatHour, HourPoint, Measure, measureValue } from './statisticsUtils';

interface HeatmapChartProps {
    /** 7 días (lunes a domingo) por 24 horas. */
    cells: HourPoint[][];
    measure: Measure;
    ariaLabel: string;
    /** Cómo leerlo, para el lector de pantalla (no se ve). */
    description?: string;
}

const CHART_HEIGHT = 340;
const HOURS = Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, '0'));
/** Escalones de la escala: del más claro (poco movimiento) al más oscuro. */
const STEPS = 5;

/**
 * Mapa de calor del día de la semana por hora. Cuanto más oscuro, más movimiento (o más espera o duración). Las celdas
 * sin atenciones van en gris. La escala sale de dos tokens teal; el detalle de cada celda está en el tooltip.
 * TODO(design-system): gráficos y mapa de calor. Novarium no define la escala de colores del mapa de calor.
 */
export default function HeatmapChart({ cells, measure, ariaLabel, description }: HeatmapChartProps) {
    const intl = useIntl();
    const ref = useRef<HTMLDivElement>(null);
    const tokens = useChartTokens(ref);

    // Apex dibuja la primera serie abajo: se invierte para que el lunes quede arriba.
    const rows = useMemo(() => {
        const days = weekdayNames(intl);
        return cells.map((row, index) => ({ day: days[index], points: row })).reverse();
    }, [intl, cells]);

    const series = useMemo(() => rows.map(row => ({
        name: row.day,
        data: row.points.map(point => ({ x: HOURS[point.hour], y: measureValue(point, measure) ?? 0 })),
    })), [rows, measure]);

    const options = useMemo<ApexOptions | null>(() => {
        if (!tokens) return null;
        const max = Math.max(0, ...cells.flat().map(point => measureValue(point, measure) ?? 0));
        const unit = measure === 'attentions' ? '' : ' min';
        const step = max / STEPS;
        const ranges = [
            { from: 0, to: 0, color: tokens.divider, name: intl.formatMessage({ id: 'ServicesStatisticsNoAttentions' }) },
            ...Array.from({ length: STEPS }, (_, index) => ({
                from: index === 0 ? Number.MIN_VALUE : index * step,
                to: (index + 1) * step,
                color: mixHex(tokens.teal300, tokens.teal800, index / (STEPS - 1)),
                name: intl.formatMessage({ id: 'ServicesStatisticsHeatRange' },
                    { from: formatDecimal(index * step), to: `${formatDecimal((index + 1) * step)}${unit}` }),
            })),
        ];
        return {
            chart: {
                type: 'heatmap',
                height: CHART_HEIGHT,
                fontFamily: tokens.font,
                foreColor: tokens.text2,
                toolbar: { show: false },
                zoom: { enabled: false },
                animations: { enabled: false },
            },
            plotOptions: {
                heatmap: { radius: 3, enableShades: false, useFillColorAsStroke: false, colorScale: { ranges } },
            },
            dataLabels: { enabled: false },
            stroke: { width: 2, colors: [tokens.card] },
            legend: { show: true, position: 'bottom', horizontalAlign: 'left', fontSize: '12px', labels: { colors: tokens.text2 } },
            xaxis: {
                type: 'category',
                axisBorder: { show: false },
                axisTicks: { show: false },
                tooltip: { enabled: false },
                labels: { rotate: 0, hideOverlappingLabels: true, style: { fontSize: '12px' } },
                title: {
                    text: intl.formatMessage({ id: 'ServicesStatisticsAxisHour' }),
                    style: { fontSize: '12px', fontWeight: 500, color: tokens.text2 },
                },
            },
            yaxis: { labels: { style: { fontSize: '12px' } } },
            tooltip: {
                custom: ({ seriesIndex, dataPointIndex }: { seriesIndex: number; dataPointIndex: number }) => {
                    const point = rows[seriesIndex].points[dataPointIndex];
                    const title = intl.formatMessage({ id: 'ServicesStatisticsHeatTitle' },
                        { day: rows[seriesIndex].day, hour: formatHour(point.hour) });
                    return `<div class="ss-tip"><div class="ss-tip__title">${title}</div>`
                        + `<div>${hourValueText(intl, point, measure)}</div></div>`;
                },
            },
        };
    }, [intl, tokens, cells, rows, measure]);

    return (
        <div ref={ref} className="ss-chart" role="img" aria-label={ariaLabel} aria-description={description}>
            {options && (
                <ReactApexChart key={measure} type="heatmap" height={CHART_HEIGHT} series={series} options={options} />
            )}
        </div>
    );
}
